<template>
  <tooltip-hover
    v-if="simpleMode"
    :text="`${subscribed ? $t('unfollow') : $t('follow')} ${user?.username}`"
    :position="position"
  >
    <base-button
      small
      :secondary="!subscribed"
      :primary="subscribed"
      :hollow="subscribed"
      :disabled="disabled"
      :icon="subscribed ? 'user-minus' : 'user-plus'"
      @click.prevent="onClick"
    />
  </tooltip-hover>
  <base-button
    v-else
    auto-width
    primary
    :small="small"
    :disabled="disabled"
    :hollow="subscribed"
    :icon="subscribed ? 'user-minus' : 'user-plus'"
    @click="onClick"
  >
    {{ subscribed ? $t('unfollow') : $t('follow') }}
  </base-button>
</template>

<script>
import { unsubscribeFromUserRequest, subscribeToUserRequest } from '@services'
import { userFollowingStore, userStatsStore, authStore } from '@stores'
import { useAsync, useRoute, addToast, useModal } from '@composables'
import TooltipHover from '@components/Message/TooltipHover.vue'
import { apiErrorsToArray } from '@helpers/utils.js'
import { computed, toRefs } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  USER_ALREADY_FOLLOWED,
  USER_NOT_FOLLOWED,
} from '@shared/js/config/errors.js'

export default {
  components: {
    TooltipHover,
  },
  props: {
    user: {
      type: Object,
      default: null,
    },
    secondary: {
      type: Boolean,
      default: false,
    },
    simpleMode: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    small: {
      type: Boolean,
      default: false,
    },
    position: {
      type: String,
      default: 'top left',
    },
  },
  setup(props) {
    const { user } = toRefs(props)

    const { t } = useI18n()
    const { user: myUser, isLoggedIn } = authStore()
    const { getUserStats, addUserStats } = userStatsStore()
    const { getGameId, getUserNameId } = useRoute()
    const { showUserLogin } = useModal()
    const { followedUsers, addUserFollow, removeUserFollow } =
      userFollowingStore()

    const userNameId = getUserNameId().value
    const gameNameId = getGameId().value
    const jointId = `${userNameId}${gameNameId || ''}`

    const subscribed = computed(
      () => !!followedUsers.value.find((sub) => sub.id === user.value?.id)
    )

    const {
      errorRef: errorRefSub,
      error: errorSub,
      run: runSub,
    } = useAsync(() =>
      subscribeToUserRequest(
        myUser.value.info?.id || myUser.value.id,
        user.value.id
      )
    )

    const {
      errorRef: errorRefUnsub,
      error: errorUnsub,
      run: runUnsub,
    } = useAsync(() =>
      unsubscribeFromUserRequest(
        myUser.value.info?.id || myUser.value.id,
        user.value.id
      )
    )

    function onClick() {
      if (isLoggedIn.value) handleSubscribe()
      else showUserLogin()
    }

    async function handleSubscribe() {
      if (subscribed.value) {
        // Unsubscribe
        removeUserFollow(user.value)

        _updateFollowerCount(-1)

        await runUnsub()

        if (errorUnsub.value && errorRefUnsub.value !== USER_NOT_FOLLOWED) {
          removeUserFollow(user.value)
          _updateFollowerCount(1)
          addToast({
            title: t('unfollow_error'),
            isError: true,
            text: apiErrorsToArray(errorUnsub.value).join(' '),
            errorRef: errorRefUnsub.value,
          })
        }
      } else {
        // Subscribe
        addUserFollow(user.value)

        _updateFollowerCount(1)

        await runSub()

        if (errorSub.value && errorRefSub.value !== USER_ALREADY_FOLLOWED) {
          removeUserFollow(user.value)
          _updateFollowerCount(-1)
          addToast({
            title: t('follow_error'),
            isError: true,
            text: apiErrorsToArray(errorSub.value).join(' '),
            errorRef: errorRefSub.value,
          })
        }
      }
    }

    function _updateFollowerCount(count) {
      addUserStats(
        jointId,
        {
          follower_count: Math.max(
            0,
            (getUserStats(jointId, !!gameNameId).value?.follower_count || 0) +
              count
          ),
        },
        !!gameNameId
      )
    }

    return {
      subscribed,
      onClick,
    }
  },
}
</script>
